import * as Sentry from '@sentry/react';
import {
    isPhiFieldName,
    REDACTED,
    scrubPhiPatterns,
    stripQueryString,
} from '@therapylake/package/sentry-shared';

const AUTH_HEADERS = new Set([
    'authorization',
    'cookie',
    'set-cookie',
    'access-token',
    'x-access-token',
    'x-auth-token',
]);

export function initSentry() {
    const dsn = process.env.REACT_APP_SENTRY_DSN;
    if (!dsn) {
        console.log('[Sentry] REACT_APP_SENTRY_DSN not set — error monitoring disabled');
        return;
    }

    Sentry.init({
        dsn,
        environment: process.env.REACT_APP_ENV || 'unknown',
        release: process.env.REACT_APP_GIT_SHA || undefined,
        tracesSampleRate: 0.1,
        sendDefaultPii: false,
        maxValueLength: 250,
        // HIPAA: Session Replay strictly off — captures actual screen pixels
        replaysSessionSampleRate: 0,
        replaysOnErrorSampleRate: 0,
        beforeSend,
        beforeBreadcrumb,
    });
}

function beforeSend(event) {
    if (event.request) {
        delete event.request.data;
        delete event.request.cookies;
        event.request.url = stripQueryString(event.request.url);
        delete event.request.query_string;

        if (event.request.headers) {
            const headers = event.request.headers;
            for (const key of Object.keys(headers)) {
                if (AUTH_HEADERS.has(key.toLowerCase())) {
                    delete headers[key];
                }
            }
        }
    }

    if (event.message) {
        event.message = scrubPhiPatterns(event.message);
    }

    if (event.exception?.values) {
        for (const exc of event.exception.values) {
            if (exc.value) {
                exc.value = scrubPhiPatterns(exc.value);
            }
            if (exc.stacktrace?.frames) {
                for (const frame of exc.stacktrace.frames) {
                    if (frame.vars) {
                        for (const key of Object.keys(frame.vars)) {
                            if (isPhiFieldName(key)) {
                                frame.vars[key] = REDACTED;
                            }
                        }
                    }
                }
            }
        }
    }

    return event;
}

function beforeBreadcrumb(breadcrumb) {
    if (breadcrumb.category === 'console') {
        return null;
    }

    if (breadcrumb.category === 'http' || breadcrumb.category === 'fetch' || breadcrumb.category === 'xhr') {
        if (breadcrumb.data) {
            if (typeof breadcrumb.data.url === 'string') {
                breadcrumb.data.url = stripQueryString(breadcrumb.data.url);
            }
            delete breadcrumb.data.request_body;
            delete breadcrumb.data.response_body;
        }
    }

    return breadcrumb;
}

/**
 * Tag the active Sentry scope with user + org context after login.
 * Call from the login saga / auth bootstrap once a JWT is decoded.
 */
export function setSentryUserContext({ userId, orgId }) {
    if (userId) {
        Sentry.setUser({ id: userId });
    }
    if (orgId) {
        Sentry.setTag('orgId', orgId);
    }
}

/**
 * Decode a JWT (no verification — payload only) and tag Sentry with the embedded
 * `userId`/`_id` and `orgId` claims. Safe no-op for malformed / missing tokens.
 */
export function setSentryUserContextFromToken(token) {
    if (!token || typeof token !== 'string') return;
    try {
        const payloadB64 = token.split('.')[1];
        if (!payloadB64) return;
        const padded = payloadB64.replace(/-/g, '+').replace(/_/g, '/');
        const payload = JSON.parse(atob(padded));
        setSentryUserContext({
            userId: payload.userId || payload._id,
            orgId: payload.orgId,
        });
    } catch {
        // malformed token — ignore
    }
}

export function clearSentryUserContext() {
    Sentry.setUser(null);
}

export { Sentry };
